import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AlertTriangle, Trash2 } from 'lucide-react';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * Shared "type the company name to confirm" delete modal.
 * Used from the company selection page and from user settings.
 *
 * Backend: DELETE /api/v1/auth/company/:companyId — requires Owner role.
 */
const DeleteCompanyModal = ({ show, onHide, companyId, companyName, onDeleted }) => {
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show) {
            setConfirmText('');
            setBusy(false);
            setError(null);
        }
    }, [show]);

    const canDelete = confirmText === companyName && !busy;

    const handleDelete = async () => {
        if (!canDelete) return;
        setBusy(true);
        setError(null);
        try {
            await axiosInstance.delete(getApiUrl(`auth/company/${companyId}`));
            onDeleted?.(companyId);
            onHide?.();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Kunde inte ta bort företaget.';
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertTriangle size={24} style={{ color: '#dc2626' }} />
                    Bekräfta borttagning
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '2rem' }}>
                <p style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                    Är du säker på att du vill ta bort företaget{' '}
                    <strong style={{ color: '#dc2626' }}>{companyName}</strong>?
                </p>
                <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 6,
                    padding: '1rem',
                    fontSize: '0.875rem',
                    color: '#dc2626',
                    marginBottom: '1.5rem',
                }}>
                    <strong>Varning:</strong> Denna åtgärd kan inte ångras. All data kopplad till detta företag kommer att tas bort permanent.
                </div>
                <label style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem',
                    display: 'block',
                }}>
                    Skriv företagsnamnet för att bekräfta:
                </label>
                <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={companyName}
                    disabled={busy}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        outline: 'none',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#44814E')}
                    onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                    Skriv exakt: <strong>{companyName}</strong>
                </p>
                {error && (
                    <p style={{ marginTop: '1rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: 0 }}>
                        {error}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 2rem' }}>
                <Button variant="secondary" onClick={onHide} disabled={busy} style={{ padding: '0.5rem 1rem' }}>
                    Avbryt
                </Button>
                <Button
                    variant="danger"
                    onClick={handleDelete}
                    disabled={!canDelete}
                    style={{
                        padding: '0.5rem 1rem',
                        opacity: canDelete ? 1 : 0.5,
                        cursor: canDelete ? 'pointer' : 'not-allowed',
                    }}
                >
                    <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                    {busy ? 'Tar bort...' : 'Ta bort företag'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeleteCompanyModal;
